import { useState, useRef, useEffect } from 'react';
import { Text, Input, Button, VStack, Field, useBreakpointValue } from '@chakra-ui/react';
import { useLocation } from 'wouter';
import { CollectionService } from '@/lib/collectionService';
import { toaster } from '@/lib/toaster';
import { useImportSheet } from '@/contexts/ImportSheetContext';

export default function ImportCollectionForm() {
  const { close, isOpen } = useImportSheet();
  const [collectionName, setCollectionName] = useState('');
  const [, navigate] = useLocation();
  const inputRef = useRef<HTMLInputElement>(null);

  // ssr:false required for focus management (CLAUDE.md)
  const isDesktop = useBreakpointValue({ base: false, lg: true }, { ssr: false });

  useEffect(() => {
    if (!isDesktop || !isOpen) return;
    const t = setTimeout(() => inputRef.current?.focus(), 150);
    return () => clearTimeout(t);
  }, [isDesktop, isOpen]);

  const [isCreating, setIsCreating] = useState(false);

  const handleCreate = async () => {
    setIsCreating(true);
    try {
      const data = await CollectionService.create(collectionName.trim());
      close();
      navigate(`/recipe-groups/${data.id}`);
    } catch (error) {
      toaster.create({
        title: (error as Error).message || 'Error creating collection',
        description: 'Please try again',
        type: 'error',
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <VStack gap={0} align="stretch">
      <Text fontFamily="body" fontWeight="400" fontSize="13px" color="text.primary" mb={4}>
        Organise your recipes by theme, occasion, diet or any custom idea.
      </Text>
      <Field.Root>
        <Input
          ref={inputRef}
          value={collectionName}
          onChange={(e) => setCollectionName(e.target.value)}
          placeholder="e.g. Weeknight Dinners, Summer BBQ…"
          aria-label="Collection name"
          size="md"
          bg="bg.surface"
          borderColor="border.muted"
          _hover={{ borderColor: 'accent.secondary' }}
          _focus={{
            borderColor: 'accent.emphasis',
            boxShadow: '0 0 0 1px var(--chakra-colors-accent-emphasis)',
          }}
          fontFamily="body"
        />
      </Field.Root>
      <Button
        mt={4}
        bg="accent.emphasis"
        color="white"
        _hover={{ bg: 'accent.emphasisHover' }}
        size="md"
        w="100%"
        loading={isCreating}
        loadingText="Creating…"
        disabled={!collectionName.trim()}
        onClick={handleCreate}
      >
        Create collection
      </Button>
    </VStack>
  );
}
